import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  useAllOnboardingQuestions,
  useCreateOnboardingQuestion,
  useUpdateOnboardingQuestion,
  useDeleteOnboardingQuestion,
} from '@/hooks/useOnboarding';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Pencil, Trash2, GripVertical, Type, List, CheckSquare } from 'lucide-react';

type QuestionType = 'text' | 'select' | 'multiselect';

function parseOptions(options: unknown): string[] {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === 'string') {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

const QUESTION_TYPE_CONFIG: Record<QuestionType, { label: string; icon: typeof Type }> = {
  text: { label: 'Text', icon: Type },
  select: { label: 'Single Select', icon: List },
  multiselect: { label: 'Multi Select', icon: CheckSquare },
};

export default function AdminOnboarding() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const [formData, setFormData] = useState({
    question: '',
    question_type: 'text' as QuestionType,
    options: '',
    is_required: true,
    sort_order: 0,
    is_active: true,
  });

  const { data: questions, isLoading } = useAllOnboardingQuestions();
  const createQuestion = useCreateOnboardingQuestion();
  const updateQuestion = useUpdateOnboardingQuestion();
  const deleteQuestion = useDeleteOnboardingQuestion();
  const { toast } = useToast();

  const resetForm = () => {
    setFormData({
      question: '',
      question_type: 'text',
      options: '',
      is_required: true,
      sort_order: 0,
      is_active: true,
    });
    setEditingQuestion(null);
  };

  const handleSubmit = async () => {
    try {
      const optionsArray =
        formData.question_type !== 'text' && formData.options
          ? formData.options.split('\n').filter((o) => o.trim())
          : null;

      const submitData = {
        question: formData.question,
        question_type: formData.question_type,
        options: optionsArray ? JSON.stringify(optionsArray) : null,
        is_required: formData.is_required,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingQuestion) {
        await updateQuestion.mutateAsync({
          id: editingQuestion.id,
          ...submitData,
        });
        toast({
          title: 'Question updated',
          description: 'The onboarding question has been updated.',
        });
      } else {
        await createQuestion.mutateAsync(submitData);
        toast({
          title: 'Question created',
          description: 'The onboarding question has been created.',
        });
      }
      setIsCreateOpen(false);
      resetForm();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      await deleteQuestion.mutateAsync(id);
      toast({
        title: 'Question deleted',
        description: 'The onboarding question has been removed.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (question: any) => {
    setEditingQuestion(question);
    const options = question.options
      ? parseOptions(question.options).join('\n')
      : '';
    setFormData({
      question: question.question,
      question_type: question.question_type as QuestionType,
      options,
      is_required: question.is_required,
      sort_order: question.sort_order,
      is_active: question.is_active,
    });
    setIsCreateOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Onboarding Questions
          </h1>
          <p className="text-muted-foreground text-sm">
            Customize questions asked during user onboarding
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingQuestion ? 'Edit Question' : 'Create New Question'}
              </DialogTitle>
              <DialogDescription>
                {editingQuestion
                  ? 'Update the question details below.'
                  : 'Add a new onboarding question.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="question">Question</Label>
                <Textarea
                  id="question"
                  placeholder="e.g., What industry do you work in?"
                  value={formData.question}
                  onChange={(e) =>
                    setFormData({ ...formData, question: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Question Type</Label>
                <Select
                  value={formData.question_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, question_type: value as QuestionType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(QUESTION_TYPE_CONFIG).map(([type, config]) => (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center gap-2">
                          <config.icon className="h-4 w-4" />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.question_type !== 'text' && (
                <div className="space-y-2">
                  <Label htmlFor="options">Options (one per line)</Label>
                  <Textarea
                    id="options"
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                    value={formData.options}
                    onChange={(e) =>
                      setFormData({ ...formData, options: e.target.value })
                    }
                    rows={5}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Required</Label>
                  <p className="text-sm text-muted-foreground">
                    Users must answer this question
                  </p>
                </div>
                <Switch
                  checked={formData.is_required}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_required: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Show this question during onboarding
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !formData.question ||
                  createQuestion.isPending ||
                  updateQuestion.isPending
                }
              >
                {createQuestion.isPending || updateQuestion.isPending
                  ? 'Saving...'
                  : editingQuestion
                  ? 'Update'
                  : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Questions</CardTitle>
          <CardDescription>
            {questions?.length || 0} questions configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {questions
                  ?.sort((a, b) => a.sort_order - b.sort_order)
                  .map((question) => {
                    const typeConfig =
                      QUESTION_TYPE_CONFIG[question.question_type as QuestionType];
                    const TypeIcon = typeConfig?.icon || Type;

                    return (
                      <TableRow key={question.id}>
                        <TableCell>
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{question.question}</div>
                          {question.options && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {parseOptions(question.options).length} options
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeConfig?.label || question.question_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {question.is_required ? (
                            <span className="text-green-600">Yes</span>
                          ) : (
                            <span className="text-muted-foreground">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              question.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                            }`}
                          >
                            {question.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(question)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(question.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
